import { styled } from "@schaeffler-commerce/design-system";
import type React from "react";
import { type MouseEvent, useRef } from "react";

interface DraggableContainerProps {
  children: React.ReactNode;
  className?: string;
}

const DraggableContainer: React.FC<DraggableContainerProps> = ({ children, className }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isDownRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const movedRef = useRef(false); // track if drag moved enough

  const DRAG_THRESHOLD = 5; // pixels to distinguish drag from click

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container || e.button !== 0) {
      return;
    }

    isDownRef.current = true;
    movedRef.current = false;
    container.classList.add("active");
    startXRef.current = e.pageX - container.offsetLeft;
    scrollLeftRef.current = container.scrollLeft;

    e.preventDefault(); // prevent link navigation on drag start
  };

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container || !isDownRef.current) {
      return;
    }

    const x = e.pageX - container.offsetLeft;
    const walk = x - startXRef.current;

    if (Math.abs(walk) > DRAG_THRESHOLD) {
      movedRef.current = true; // mark as drag
    }

    container.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleMouseUp = (e: MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.classList.remove("active");

    if (movedRef.current) {
      // prevent anchor navigation after drag
      e.preventDefault();
      e.stopPropagation();

      // prevent the action that fires on click inside
      // clickHandler uses globalThis.MouseEvent: This is the native DOM MouseEvent, not React’s synthetic event.
      const clickHandler = (clickEvent: globalThis.MouseEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        container.removeEventListener("click", clickHandler, true);
      };

      container.addEventListener("click", clickHandler, true);
    }

    isDownRef.current = false;
  };

  const handleMouseLeave = () => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    isDownRef.current = false;
    container.classList.remove("active");
  };

  return (
    <Container
      className={className}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </Container>
  );
};

export default DraggableContainer;

const Container = styled("div")({
  display: "flex",
  gap: 1,
  overflowX: "auto",
  scrollbarWidth: "none",
  cursor: "grab",
  userSelect: "none",
  "&.active": { cursor: "grabbing" },
});
